import React, { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useAxios } from "../../../common_nextgen/axios/AxiosHelper";
import { fetchDropdownValues } from "../../../common_nextgen/common_components/dropdown/DropdownHelper";
import { IndicatorCell } from "../../../common_nextgen/common_components/IndicatorCell/IndicatorCell";
import useCustomNavigate from "../../../common_nextgen/common_components/Navigation/NextGenNavigation";
import { useToast } from "../../../common_nextgen/hooks/ToastContext";
import { PRICE_SCHEDULE_MODES, ROUTE_PATHS } from "../../../common_nextgen/utils/Constants";
import {
  downloadCSV,
  fetchDataCallOnInitialLoad,
} from "../../../common_nextgen/utils/UtilFunctions";
import { useRBAC } from "../../../common_nextgen/hooks/RBACContext";

const usePatientPaySchedule = (
  onSelect,
  hideModal,
  initialSelectParams,
  initialSelectMode,
  initialLookupType,
  selectParamsData,
  onNavigate
) => {
  const [data, setData] = useState([]); /* Price Schedule Data */
  const {writeAccess} = useRBAC();
  const location = useLocation();
  const stateVal = location.state ?? [];
  const [currentState = {}] = [...stateVal].reverse();
  const previousState = [...stateVal];
  previousState.splice(stateVal.length - 1, 1);
  const {
    currentIndex: initialIndex = 0,
    paramsData: initialParamsData = {
      scheduleName: "",
      description: "",
      hiddenRecordIndicator: "Active",
    },
  } = currentState;

  const baseParamsData = initialLookupType
    ? { ...selectParamsData, hiddenRecordIndicator: "Active" }
    : initialParamsData;
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [paramsData, setParamsData] = useState(baseParamsData);
  const { axios_helper_no_header } = useAxios();
  const toast = useToast();

  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [rowData, setRowData] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedRows, setSelectedRows] = useState([]);
  const navigateToPage = useCustomNavigate();
  const [dropdownValues, setDropdownValues] = useState({});
  const [disabledDropdown, setDisabledDropdown] = useState(null);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState("no");
  const [isHideConfirmationModalOpen, setIsHideConfirmationModalOpen] =
    useState(false);
  const [hideSelectedOption, setHideSelectedOption] = useState("no");

  const {
    PATIENT_PAY_SCHEDULE_ADD,
    PATIENT_PAY_SCHEDULE_DISPLAY,
    PATIENT_PAY_SCHEDULE_EDIT,
    COPY_PATIENT_PAY_SCHEDULE,
    PATIENT_PAY_NOTE,
    PATIENT_PAY_CLIENT_LIMITS,
    CLIENT_PATIENT_PAY_SCHEDULE,
    PATIENT_PAY_SCHEDULE_CRITERIA,
  } = ROUTE_PATHS;

  const [selectParams, setSelectParams] = useState(initialSelectParams);
  const [selectMode, setSelectMode] = useState(initialSelectMode);
  const [lookupType, setLookupType] = useState(initialLookupType);

  const screen_title = "Patient Pay Schedule";
  const screenNumber = selectMode ? "RCPS3001" : "RCPS3003";

  const isSafeKey = (k) =>
  typeof k === "string" && /^[a-zA-Z0-9_]+$/.test(k);

  /**
    * Encode all the string type request params.
    *
    * @param {*} newParams
    * @returns encoded request params
    */
    const encodeParams = (rawParams) => {
      if (
        rawParams &&
        typeof rawParams === "object" &&
        Object.keys(rawParams || {}).length
      ) {
        Object.keys(rawParams).forEach((key) => {
          if (isSafeKey(key) && typeof rawParams[key] === "string") {
            rawParams[key] = encodeURIComponent(rawParams[key]);
          }
        });
      }
      return rawParams;
    };

  /* Search form fields that needs to be shown for filtering */
  const formFields = [
    {
      fieldLabel: "PS3 Patient Pay Sch Name",
      name: "scheduleName",
      label: "Schedule",
      type: "text",
      fieldNature: "editable",
      value: "",
      maxLength: 10,
    },
    {
      fieldLabel: "PS3 Patient Pay Sch Desc",
      name: "description",
      label: "Description",
      type: "text",
      fieldNature: "editable",
      value: "",
      maxLength: 25,
    },
    {
      fieldLabel: "Hidden Record IndicatorValid Values",
      name: "hiddenRecordIndicator",
      label: "Status",
      type: "dropdown",
      fieldNature: "editable",
      value: "",
      maxLength: 1,
    },
  ];

  const dropdownOptions = [
    { fieldLabel: "Hidden Record Indicator", condition: "Valid Values" },
  ];
  const dropdownCondition = {
    dropDowns: dropdownOptions.map((item) => item.fieldLabel + item.condition),
  };

  /* ACTION BUTTONS info that needs to be displayed on the page */
  const ctaButtons = selectMode
    ? [
        {
          button_label: "Export as CSV",
          button_handeler: () => {
            exportAsCSV();
          },
          button_key: "",
        },
      ]
    : [
        {
          button_label: "Add Patient Pay Schedule",
          button_handeler: () => {
            navigateToPage(PATIENT_PAY_SCHEDULE_ADD, [
              {
                previousScreen: location.pathname,
              },
            ]);
          },
          button_key: "F6",
        },
        {
          button_label: "Export as CSV",
          button_handeler: () => {
            exportAsCSV();
          },
          button_key: "",
        },
      ];

  const leftButtons = selectMode
    ? [
        {
          button_label: "Back",
          button_handeler: () => {
            if (lookupType === "local") {
              hideModal();
            } else {
              const message = {
                type: "hideModal",
              };
              window.parent.postMessage(message, "*");
            }
          },
          button_key: "F12",
        },
      ]
    : [];

  /* Gear Icon Menu option CTAs */
  const menuOptions = selectMode
    ? [
        {
          label: "Select",
          icon: <span className="material-icons">add</span>,
          command: () => {
            const paramDataMap = new Map();
            selectParams.forEach((key) => {
            if (rowData.hasOwnProperty(key["target"])) {
              if (key["source"]) {
                  paramDataMap.set(key["source"], rowData[key["target"]]);
              }
            }
            });
            const paramData = Object.fromEntries(paramDataMap);
            console.log("paramData orginal ---- : ", paramData);
            if (lookupType === "local") {
              onSelect(paramData);
            } else {
              const message = {
                type: "responseData",
                data: paramData,
              };
              window.parent.postMessage(message, "*");
            }
            const paramData1 = new Map();
            selectParams.forEach(key => {
              if (rowData.hasOwnProperty(key["target"])) {
                paramData1.set(key["source"], rowData[key["target"]]);
              }
            });
            const paramDataObject = Object.fromEntries(paramData1);
            console.log("paramData paramDataObject ---- : ", paramDataObject);
          },
        },
        {
          label: "Display",
          icon: <span className="material-icons">visibility</span>,
          command: () => {
            /* const message = {
              message: "navigate-to-presto",
              programCode: "RCPS3004I",
              parameters: {
                ps3PatientPaySchName: rowData.ps3PatientPaySchName,
                ps3PatientPaySchDesc: rowData.ps3PatientPaySchDesc,
              },
            };
            window.top?.postMessage(message, "*");
          */
            console.log("rowData", rowData);
            if (lookupType === "external") {
              const params = selectParams || [];
              const paramDataMap = new Map();
              params.forEach((item) => {
              if (rowData.hasOwnProperty(item["target"])) {
                  paramDataMap.set(item["source"], rowData[item["target"]]);
                }
              });

              const paramData = Object.fromEntries(paramDataMap);
              const message = {
                type: "navigateData",
                data: { ...rowData },
                target: "PATIENT_PAY_SCHEDULE_READ_ONLY",
                schedulesDataKey: "selectedPatients",
              };
              console.log("display message", message);
              window.parent?.postMessage(message, "*");
            } else {
              onNavigate({
                data: { ...rowData },
                target: ROUTE_PATHS.PATIENT_PAY_SCHEDULE_DISPLAY,
                navType: "local",
              });
            }
          },
        },
        {
          label: "Client Pat Pay",
          icon: <span className="material-icons">person</span>,
          command: () => {
            /* const message = {
              message: "navigate-to-presto",
              programCode: "RCPS6001I",
              parameters: {
                ps3PatientPaySchName: rowData.ps3PatientPaySchName,
              },
            };
            window.top?.postMessage(message, "*");
          */ if (lookupType === "external") {
            const params = selectParams || [];
            const paramDataMap = new Map();
            params.forEach((item) => {
              if (rowData.hasOwnProperty(item["target"])) {
                paramDataMap.set(item["source"], rowData[item["target"]]);
              }
            });
            const paramData = Object.fromEntries(paramDataMap);
              const message = {
                type: "navigateData",
                data: { ...rowData },
                target: "CLIENT_PATIENT_PAY_SCHEDULE",
                schedulesDataKey: "selectedPatients",
              };
              console.log("display message", message);
              window.parent?.postMessage(message, "*");
            } else {
              onNavigate({
                data: { ...rowData },
                target: ROUTE_PATHS.CLIENT_PATIENT_PAY_SCHEDULE,
                navType: "local",
              });
            }
          },
        },
        {
          label: "Client Limits",
          icon: <span className="material-icons">search</span>,
          command: () => {
            /*const message = {
              message: "navigate-to-presto",
              programCode: "RCPS2001",
              parameters: {
                ps3PatientPaySchName: rowData.ps3PatientPaySchName,
                patPayScheduleCde: rowData.ps3PatientPaySchName,
                ps3PatientPaySchDesc: rowData.ps3PatientPaySchDesc,
                workInitialProgramMode: "DSP",
              },
            };
            window.top?.postMessage(message, "*");
            */
            if (lookupType === "external") {
              const params = selectParams || [];
              const paramDataMap = new Map();
              params.forEach(item => {
              if (rowData.hasOwnProperty(item["target"])) {
                paramDataMap.set(item["source"], rowData[item["target"]]);
                } 
              });
              const paramData = Object.fromEntries(paramDataMap);
              const message = {
                type: "navigateData",
                data: { ...rowData },
                target: "PATIENT_PAY_CLIENT_LIMITS",
                schedulesDataKey: "selectedPatients",
              };
              console.log("display message", message);
              window.parent?.postMessage(message, "*");
            } else {
              onNavigate({
                data: { ...rowData },
                target: ROUTE_PATHS.PATIENT_PAY_CLIENT_LIMITS,
                navType: "local",
              });
            }
          },
        },
        {
          label: "Criteria",
          icon: <span className="material-icons">filter_list</span>,
          command: () => {
            /* const message = {
              message: "navigate-to-presto",
              programCode: "RCPPC002I",
              parameters: {
                ps3PatientPaySchName: rowData.ps3PatientPaySchName,
                ps3PatientPaySchDesc: rowData.ps3PatientPaySchDesc,
                ps3PatientPaySchDesc: rowData.ps3PatientPaySchDesc,
              },
            };
            window.top?.postMessage(message, "*");
            */ if (lookupType === "external") {
              const params = selectParams || [];
              const paramDataMap = new Map();
              params.forEach((item) => {
                if (rowData.hasOwnProperty(item["target"])) {
                  paramDataMap.set(item["source"], rowData[item["target"]]);
                }
              });
              const paramData = Object.fromEntries(paramDataMap);
              const message = {
                type: "navigateData",
                data: { ...rowData },
                target: "PATIENT_PAY_SCHEDULE_CRITERIA",
                schedulesDataKey: "selectedPatients",
              };
              console.log("display message", message);
              window.parent?.postMessage(message, "*");
            } else {
              onNavigate({
                data: { ...rowData },
                target: ROUTE_PATHS.PATIENT_PAY_SCHEDULE_CRITERIA,
                navType: "local",
              });
            }
          },
        },
      ]
    : [
        {
          label: "Note",
          icon: <span className="material-icons">note</span>,
          command: () => {
            const lastRecord = selectedRows.length
              ? selectedRows[selectedRows.length - 1]
              : rowData;
            const { ps3PatientPaySchName } = lastRecord;
            const updatedParamsData = {
              ...paramsData,
              scheduleName: ps3PatientPaySchName,
            };
            navigateToPage(PATIENT_PAY_NOTE, [
              { paramsData: updatedParamsData },
              {
                selectedPatients: selectedRows.length
                  ? selectedRows
                  : [rowData],
                previousScreen: location.pathname,
                currentIndex: 0,
              },
            ]);
          },
        },
        {
          label: "Edit",
          icon: <span className="material-icons">edit</span>,
          command: () => {
            const lastRecord = selectedRows.length
              ? selectedRows[selectedRows.length - 1]
              : rowData;
            const { ps3PatientPaySchName } = lastRecord;
            const updatedParamsData = {
              ...paramsData,
              scheduleName: ps3PatientPaySchName,
            };
            navigateToPage(PATIENT_PAY_SCHEDULE_EDIT, [
              { paramsData: updatedParamsData },
              {
                selectedPatients: selectedRows.length
                  ? selectedRows
                  : [rowData],
                previousScreen: location.pathname,
                currentIndex: 0,
              },
            ]);
          },
        },
        {
          label: "Copy",
          icon: <span className="material-icons">content_copy</span>,
          command: () => {
            const lastRecord = selectedRows.length
              ? selectedRows[selectedRows.length - 1]
              : rowData;
            const { ps3PatientPaySchName } = lastRecord;
            const updatedParamsData = {
              ...paramsData,
              scheduleName: ps3PatientPaySchName,
            };
            navigateToPage(COPY_PATIENT_PAY_SCHEDULE, [
              { paramsData: updatedParamsData },
              {
                selectedPriceSchedules: selectedRows.length
                  ? selectedRows
                  : [rowData],
                previousScreen: location.pathname,
                paramsData: {
                  ...paramsData,
                  hiddenRecordIndicator:
                    selectedRows.length > 0
                      ? paramsData.hiddenRecordIndicator
                      : "A",
                },
              },
            ]);
          },
        },
        {
          label: "Delete",
          icon: <span className="material-icons">delete</span>,
          command: () => {
            openConfirmationModal();
          },
        },
        {
          label: "Display",
          icon: <span className="material-icons">visibility</span>,
          command: () => {
            const lastRecord = selectedRows.length
              ? selectedRows[selectedRows.length - 1]
              : rowData;
            const { ps3PatientPaySchName } = lastRecord;
            const updatedParamsData = {
              ...paramsData,
              scheduleName: ps3PatientPaySchName,
            };
            navigateToPage(PATIENT_PAY_SCHEDULE_DISPLAY, [
              { paramsData: updatedParamsData },
              {
                selectedPatients: selectedRows.length
                  ? selectedRows
                  : [rowData],
                previousScreen: location.pathname,
                currentIndex: 0,
              },
            ]);
          },
        },
        {
          label: "Client Pat Pay",
          icon: <span className="material-icons">person</span>,
          command: () => {
            const lastRecord = selectedRows.length
              ? selectedRows[selectedRows.length - 1]
              : rowData;
            const { ps3PatientPaySchName } = lastRecord;
            const updatedParamsData = {
              ...paramsData,
              scheduleName: ps3PatientPaySchName,
            };
            navigateToPage(CLIENT_PATIENT_PAY_SCHEDULE, [
              { paramsData: updatedParamsData },
              {
                selectedPatients: selectedRows.length
                  ? selectedRows
                  : [rowData],
                previousScreen: location.pathname,
                currentIndex: 0,
              },
            ]);
          },
        },
        {
          label: "Client Limits",
          icon: <span className="material-icons">search</span>,
          command: async () => {
            if(!writeAccess){
              const params = {
                ps3PatientPaySchName: rowData?.ps3PatientPaySchName,
                ps3PatientPaySchDesc: rowData?.ps3PatientPaySchDesc
              }
              try {
                const {responseData} = await axios_helper_no_header("GET", "api/ps2patpayschclntlmts/initialFormData/RCPS2001", null, encodeParams(params));
                const {addDate} = responseData;
                if(!addDate){
                  toast.current.show({severity:"info", summary:"", detail:"Client Limits not present", life:3000});
                  return;
                }
              }
              catch(e){
                console.error(e);
               // return;
              }
            }
            const lastRecord = selectedRows.length
              ? selectedRows[selectedRows.length - 1]
              : rowData;
            const { ps3PatientPaySchName } = lastRecord;
            const updatedParamsData = {
              ...paramsData,
              scheduleName: ps3PatientPaySchName,
            };
            navigateToPage(PATIENT_PAY_CLIENT_LIMITS, [
              { paramsData: updatedParamsData },
              {
                selectedPatients: selectedRows.length
                  ? selectedRows
                  : [rowData],
                previousScreen: location.pathname,
                currentIndex: 0,
                currentMode: writeAccess ? PRICE_SCHEDULE_MODES.ADD : PRICE_SCHEDULE_MODES.READ_ONLY
              },
            ]);
          },
        },
        {
          label: "Criteria",
          icon: <span className="material-icons">filter_list</span>,
          command: () => {
            const lastRecord = selectedRows.length
              ? selectedRows[selectedRows.length - 1]
              : rowData;
            const { ps3PatientPaySchName } = lastRecord;
            const updatedParamsData = {
              ...paramsData,
              scheduleName: ps3PatientPaySchName,
            };
            navigateToPage(PATIENT_PAY_SCHEDULE_CRITERIA, [
              { paramsData: updatedParamsData },
              {
                selectedPatients: selectedRows.length
                  ? selectedRows
                  : [rowData],
                previousScreen: location.pathname,
                currentIndex: 0,
              },
            ]);
          },
        },
      ];

  if (rowData["hiddenRecordIndicator"] === "Active" && !selectMode) {
    menuOptions.push({
      label: "Hide",
      icon: <span className="material-icons">hide_source</span>,
      command: () => {
        openHideConfirmationModal();
        openHideConfirmationModal();
      },
    });
  }

  /* Data table column info. */
  const columnDef = [
    {
      header: "Schedule",
      field: "ps3PatientPaySchName",
    },
    {
      header: "Description",
      field: "ps3PatientPaySchDesc",
    },
    {
      header: "Indicators",
      field: "noteExistenceStatus",
      body: (rowData) => (
        <IndicatorCell rowData={rowData} fields={["noteExistenceStatus"]} tooltipContent={{noteExistenceStatus: "Notes"}} />
      ),
    },
    {
      header: "Status",
      field: "hiddenRecordIndicator",
    },
  ];

  /* Param Key which used as bridge between various operations on tabluar data */
  const parmKey = "ps3PatientPaySchName";

  /* Handler that fetches the data */

  const fetchData = useCallback(async () => {
    if (paramsData.hiddenRecordIndicator === undefined) {
      console.log("hidden record indicator is not available in paramsData");
      // Set hiddenRecordIndicator to "Active" by default
      paramsData.hiddenRecordIndicator = "Active";
    }

    const page = first / rows;
    const currentParams = encodeParams(paramsData);

    try {
      const raw_response = await axios_helper_no_header(
        "GET",
        `api/patientpayschedule`,
        null,
        {
          page: page,
          size: rows,
          ...currentParams,
        }
      );
      const response = raw_response.responseData;
      const pageResponse = raw_response.responsePaginationDTO;
      if (response.length === 0) {
        setData([]);
        setTotalRecords(0);
        toast.current.show({
          severity: "info",
          summary: "",
          detail: response.responseStatus.message,
          life: 3000,
        });
      } else {
        setData(response);
        setTotalRecords(pageResponse.totalRecords); // Assuming the API returns totalRecords
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setData([]);
      setTotalRecords(0);
    }
  }, [paramsData, currentPage, rows, first]);

  const fetchDropdownData = async (dataDropdown) => {
    try {
      const response = await fetchDropdownValues(dataDropdown);
      setDropdownValues(response);
    } catch (error) {
      console.error("Error fetching dropdown values:", error);
    }
  };

  useEffect(() => {
    fetchDropdownData(dropdownCondition);
  }, []);

  useEffect(() => {
    fetchDataCallOnInitialLoad(fetchData, lookupType);
  }, [paramsData, rows, currentPage, lookupType]);

  const onPageChange = (event) => {
    setFirst(event.first);
    setCurrentPage(event.page);
    setRows(event.rows);
  };

  const openConfirmationModal = () => {
    setIsConfirmationModalOpen(true);
  };

  const openHideConfirmationModal = () => {
    setIsHideConfirmationModalOpen(true);
  };

  const closeConfirmationModal = () => {
    setIsConfirmationModalOpen(false);
    setSelectedOption("no");
  };

  const closeHideConfirmationModal = () => {
    setIsHideConfirmationModalOpen(false);
    setHideSelectedOption("no");
  };

  const hidePriceSchedule = () => {
    if (hideSelectedOption === "yes") {
      const params = {
        scheduleName: rowData[parmKey],
        screenNumber: screenNumber,
      };
      axios_helper_no_header("PUT", "api/patientpayschedule/hide", null, encodeParams(params))
        .then((response) => {
          const updatedItems = data.filter((item) => item[parmKey] !== rowData);
          setTotalRecords((prevTotal) => prevTotal - 1);
          if (currentPage > 0) {
            setFirst(0);
            setCurrentPage(0);
          } else {
            fetchData();
          }
          toast.current.show({
            severity: "success",
            summary: "",
            detail: response.responseStatus.message,
            life: 3000,
          });
          closeHideConfirmationModal();
        })
        .catch((error) => {
          console.error("There was an error updating the data!", error);
          toast.current.show({
            severity: "error",
            summary: "",
            detail: error.message,
            life: 3000,
          });
          closeHideConfirmationModal();
        });
    } else {
      closeHideConfirmationModal();
    }
  };

  const deletePriceSchedule = () => {
    if (selectedOption === "yes") {
      const params = { scheduleName: rowData[parmKey] };
      axios_helper_no_header("DELETE", "api/patientpayschedule", null, encodeParams(params))
        .then((response) => {
          const updatedItems = data.filter((item) => item[parmKey] !== rowData);
          if (currentPage > 0) {
            setFirst(0);
            setCurrentPage(0);
          } else {
            fetchData();
          }
          toast.current.show({
            severity: "success",
            summary: "",
            detail: response.responseStatus.message,
            life: 3000,
          });
          closeConfirmationModal();
        })
        .catch((error) => {
          console.error("There was an error deleting the data!", error);
          const errorMessage =
            error.data?.responseStatus?.message || error.message;
          toast.current.show({
            severity: "error",
            summary: "",
            detail: errorMessage,
            life: 3000,
          });
          closeConfirmationModal();
        });
    } else {
      closeConfirmationModal();
    }
  };

  const exportAsCSV = async () => {
    const sanitizedParamsData = Object.fromEntries(
      Object.entries(paramsData).map(([key, value]) => [
        key,
        value == null ? "" : value,
      ])
    );

    const queryParams = new URLSearchParams({
      ...sanitizedParamsData,
      screenNumber: screenNumber,
    });

    try {
      const response = await axios_helper_no_header(
        "GET",
        "api/patientpayschedule/exportcsv",
        null,
        encodeParams(queryParams),
        "blob"
      );
      downloadCSV(response);
    } catch (error) {
      console.error("Error exporting as CSV:", error);
      toast.current.show({
        severity: "error",
        summary: "",
        detail: "Failed to export CSV. Please try again.",
        life: 3000,
      });
    }
  };

  const updateParamData = (data) => {
    setFirst(0);
    setCurrentPage(0);
    setParamsData(data);
  };

  const handleMessage = (event) => {
    const { type, data } = event.data;
    console.log("iframe load", type, data);
    if (type === "initData") {
      const { selectMode, data: paramData } = data;
      setLookupType("external");
      setSelectParams(paramData.params);
      const newParamsData = {
        ...paramData?.paramsData,
        scheduleName: paramData?.paramsData?.ps3PatientPaySchName,
        description: paramData?.paramsData?.ps3PatientPaySchDesc,
      };
      setParamsData(newParamsData);
      setSelectMode(selectMode);
      setDisabledDropdown({ hiddenRecordIndicator: true });
    }
  };

  useEffect(() => {
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  return {
    screenNumber,
    screen_title,
    formFields,
    dropdownValues,
    paramsData,
    updateParamData,
    ctaButtons,
    data,
    parmKey,
    menuOptions,
    columnDef,
    setRowData,
    selectedRows,
    setSelectedRows,
    first,
    rows,
    totalRecords,
    currentPage,
    onPageChange,
    isConfirmationModalOpen,
    isHideConfirmationModalOpen,
    closeConfirmationModal,
    closeHideConfirmationModal,
    deletePriceSchedule,
    hidePriceSchedule,
    setSelectedOption,
    setHideSelectedOption,
    selectMode,
    leftButtons,
    disabledDropdown,
  };
};

export default usePatientPaySchedule;
